import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { InsuranceDetails, Patient } from "~/api/patients";
import { UtmParams } from "~/utils/utmCapture";

export interface DraftState {
  insuranceGroup: string | null;
  insuranceGroupName: string | null;
  insuranceGroupResolved: boolean;
  readyToFinalize: boolean;
}

export interface SignUpStoreState {
  patient: Patient;
  showInsuranceFinalStep: boolean;
  showLocationFinalStep: boolean;
  appointmentBooked: boolean;
  draftState: DraftState;
  currentStepIndex: number;
  setCurrentStepIndex: (index: number) => void;
  setPatientField<K extends keyof SignUpStoreState["patient"]>(
    field: K,
    value: SignUpStoreState["patient"][K],
  ): void;
  setPatientInsuranceField<K extends keyof InsuranceDetails>(
    field: K,
    value: InsuranceDetails[K],
    isSecondary?: boolean,
  ): void;
  resetInsuranceFields: (providerName: string, isSecondary?: boolean) => void;
  setShowInsuranceFinalStep: (show: boolean) => void;
  setShowLocationFinalStep: (show: boolean) => void;
  setUtmParams: (params: UtmParams | null) => void;
  setAppointmentBooked: (booked: boolean) => void;
  hasRequiredPatientFields: () => boolean;
  setDraftState: (state: DraftState) => void;
  reset: () => void;
}

const initialPatient: Patient = {
  id: "",
  conditions: [],
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  referral: "",
  otherReferral: "",
  referringPracticeId: null,
  referringDoctorId: null,
  referringDoctorName: null,
  primaryInsurance: {
    providerName: "",
  },
  state: "",
  preferredOfficeVisit: "Healthie Video Call",
  utmParams: undefined,
};

export const useSignUpStore = create<SignUpStoreState>()(
  persist(
    (set, get) => ({
      patient: initialPatient,
      showInsuranceFinalStep: false,
      showLocationFinalStep: false,
      appointmentBooked: false,
      draftState: {
        insuranceGroup: null,
        insuranceGroupName: null,
        insuranceGroupResolved: false,
        readyToFinalize: false,
      },
      currentStepIndex: 0,
      setCurrentStepIndex: (index) => {
        set(() => ({ currentStepIndex: index }));
      },
      setPatientField: (field, value) => {
        set((state) => ({
          patient: {
            ...state.patient,
            [field]: value,
          },
        }));
      },
      setPatientInsuranceField: (field, value, isSecondary = false) => {
        set((state) => ({
          patient: {
            ...state.patient,
            [isSecondary ? "secondaryInsurance" : "primaryInsurance"]: {
              ...state.patient[
                isSecondary ? "secondaryInsurance" : "primaryInsurance"
              ],
              [field]: value,
            },
          },
        }));
      },
      resetInsuranceFields: (providerName, isSecondary = false) => {
        set((state) => ({
          patient: {
            ...state.patient,
            [isSecondary ? "secondaryInsurance" : "primaryInsurance"]: {
              providerName,
            },
          },
        }));
      },
      setShowInsuranceFinalStep: (show) => {
        set(() => ({ showInsuranceFinalStep: show }));
      },
      setShowLocationFinalStep: (show) => {
        set(() => ({ showLocationFinalStep: show }));
      },
      setAppointmentBooked: (booked) => {
        set(() => ({ appointmentBooked: booked }));
      },
      setUtmParams: (params) => {
        set((state) => ({
          patient: {
            ...state.patient,
            utmParams: params ?? undefined,
          },
        }));
      },
      setDraftState: (draftState) => {
        set(() => ({ draftState }));
      },
      hasRequiredPatientFields: () => {
        const { patient } = get();
        return !!(
          patient.id &&
          patient.email &&
          patient.state &&
          patient.conditions &&
          patient.primaryInsurance
        );
      },
      reset: () => {
        const currentUtmParams = get().patient.utmParams;
        set(() => ({
          patient: {
            ...initialPatient,
            utmParams: currentUtmParams,
          },
          showInsuranceFinalStep: false,
          showLocationFinalStep: false,
          appointmentBooked: false,
          draftState: {
            insuranceGroup: null,
            insuranceGroupName: null,
            insuranceGroupResolved: false,
            readyToFinalize: false,
          },
          currentStepIndex: 0,
        }));
      },
    }),
    {
      name: "signup-form-storage",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        patient: state.patient,
        showInsuranceFinalStep: state.showInsuranceFinalStep,
        showLocationFinalStep: state.showLocationFinalStep,
        appointmentBooked: state.appointmentBooked,
        draftState: state.draftState,
        currentStepIndex: state.currentStepIndex,
      }),
    },
  ),
);
